import { useState, useEffect } from 'react';

import { useSearchParams } from 'react-router-dom';
import { TldrawUiDialogCloseButton, TldrawUiDialogBody, TldrawUiDialogTitle } from 'tldraw';

import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Button } from '@/components/atoms/new-button';
import Select from '@/components/atoms/Select';
import { Textarea } from '@/components/atoms/textarea';
import { MyOptionType } from '@/types';

interface NodeEditingModalProps {
  onClose: () => void;
  onSave: (nodeData: { dateText: string; description: string; documentNames: string }) => void;
  initialData: {
    dateText: string;
    description: string;
    documentNames: string;
  };
}

export const NodeEditingModal = ({ onClose, onSave, initialData }: NodeEditingModalProps) => {
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');

  const [date, setDate] = useState(initialData.dateText);
  const [description, setDescription] = useState(initialData.description);
  const [selectedDoc, setSelectedDoc] = useState<MyOptionType>(null);

  const { data: allDocuments } = useGetDocIds(caseId);

  // Initialize selected document based on initial document names
  useEffect(() => {
    if (initialData.documentNames && allDocuments) {
      const documentName = initialData.documentNames.split(', ')[0]; // Take first document only
      const initialSelectedDoc = allDocuments?.documents?.filter(Boolean).find((doc) => doc.file.name === documentName);

      if (initialSelectedDoc) {
        setSelectedDoc({
          label: initialSelectedDoc.file.name,
          value: initialSelectedDoc.id,
        });
      }
    }
  }, [initialData.documentNames, allDocuments]);

  const handleSave = () => {
    if (description.trim()) {
      const documentNames = selectedDoc?.label || '';
      onSave({
        dateText: date.trim(),
        description: description.trim(),
        documentNames: documentNames,
      });
    }
  };

  const handleClose = () => {
    onClose();
  };

  const documentOptions =
    allDocuments?.documents?.filter(Boolean).map((doc) => ({
      label: doc.file.name,
      value: doc.id,
    })) || [];

  return (
    <>
      <div className="border-b border-gray-200 px-7 py-2">
        <div className="flex items-center justify-between">
          <TldrawUiDialogTitle>
            <p className="text-base font-semibold text-gray-900">Edit Chronology Event</p>
          </TldrawUiDialogTitle>
          <TldrawUiDialogCloseButton />
        </div>
      </div>
      <TldrawUiDialogBody className="w-[450px]">
        <div className="px-4 py-1">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="date">Date</Label>
              <Input
                type="text"
                id="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                placeholder="e.g., January 15, 2023"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="document">Document</Label>
              <Select
                options={documentOptions}
                selectedOption={selectedDoc}
                onChange={(newValue) => setSelectedDoc(newValue)}
                placeholder={'Select a document'}
                className="w-full rounded-md bg-white outline-none"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter event description..."
                rows={3}
                className="resize-none"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-between gap-3">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={!description.trim()}>
              Save Changes
            </Button>
          </div>
        </div>
      </TldrawUiDialogBody>
    </>
  );
};
